// 3rd Party
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import { SPECIALTY } from 'common/constants';
import {
  ColorIcon,
  Div,
  FlexRow,
  H2,
  MarketingButton,
} from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import { requestServicesForSpecialty } from 'connect/actions';
import { DIMENSIONS } from 'providers/WindowDimensionsProvider';

import {
  selectDataMatchId,
  selectDataProfileLink,
  selectDataUser,
} from '../../../../connect/selectors';
import DentistChairPersonSvg from '../../../common/components/svgs/reason-icons/DentistChairPersonSvg';
import ToothBrushSvg from '../../../common/components/svgs/reason-icons/ToothBrushSvg';
import UrgentSvg from '../../../common/components/svgs/reason-icons/UrgentSvg';

// Other

import { DENTAL_REASON_ID } from '../../constants/constants';
import {
  filterAndSortServices,
  filterPickedServices,
  getAllClinicServiceIds,
  listAllServices,
  markServicesWithOfferedAndPicked,
} from '../../helpers/profile-need-to-know-helpers';

const getServiceIcon = iconName => {
  switch (iconName) {
    case DENTAL_REASON_ID.checkup:
      return <ToothBrushSvg color={colors.sage} height="55px" />;
    case DENTAL_REASON_ID.services:
      return <DentistChairPersonSvg color={colors.sage} height="55px" />;
    case DENTAL_REASON_ID.urgent:
      return <UrgentSvg color={colors.sage} height="55px" />;
    default:
      return undefined;
  }
};

const ServicesNeedToKnow = ({
  layout,
  primarySpecialty,
  providers,
  reasonExists,
  subReasonIds,
  visitReason,
}) => {
  const dispatch = useDispatch();
  const [showAllServices, setShowAllServices] = useState(false);
  const matchId = useSelector(selectDataMatchId);
  const directLink = useSelector(selectDataProfileLink);
  const user = useSelector(selectDataUser);
  const {
    services: allServices,
    specialtyId: specialtyIdForServices,
  } = useSelector(state => state.data.servicesForSpecialty);
  const allServicesFiltered = filterAndSortServices(
    allServices,
    primarySpecialty === SPECIALTY.dentist,
  );
  const clinicServices = getAllClinicServiceIds(providers);
  const allServicesMarked =
    markServicesWithOfferedAndPicked(
      clinicServices,
      allServicesFiltered,
      visitReason,
      subReasonIds,
    ) || [];
  const subReasonsMarked =
    markServicesWithOfferedAndPicked(
      clinicServices,
      allServicesFiltered,
      subReasonIds,
    ) || [];
  const visitReasonIsServices =
    _.get(visitReason, 'overall.id') === DENTAL_REASON_ID.services;
  const selectedServices = filterPickedServices(allServicesMarked);

  /* Coming from partner funnel, you get partner object. If you come from normal funnel,
   user seems to be set to undefined by the {}. Trying to set it to a falsy value, gives TypeError cannot read property partner of null */
  const { partner } = user || {};

  useEffect(() => {
    if (primarySpecialty && primarySpecialty !== specialtyIdForServices) {
      dispatch(requestServicesForSpecialty(primarySpecialty));
    }
  }, [primarySpecialty]);

  return matchId && !directLink && reasonExists
    ? (
      <div>
        {showAllServices || partner
          ? (
            <>
              <FlexRow flexWrap="wrap" margin="0 0 0 20px" padding="0" as="ul">
                {visitReasonIsServices && subReasonsMarked.length > 0
                  ? listAllServices(subReasonsMarked)
                  : listAllServices(allServicesMarked)}
              </FlexRow>
              {!partner
                && (
                  <MarketingButton
                    type="secondary"
                    shape="round"
                    margin="20px 0 0 0"
                    onClick={() => setShowAllServices(false)}
                  >
                    Hide All Practice Services
                  </MarketingButton>
                )
              }
            </>
          )
          : (
            <>
              <FlexRow>
                <Div margin="0 30px 0 0" width="40px">
                  {getServiceIcon(_.get(visitReason, 'overall.id'))}
                </Div>
                <div className="sensitive-data">
                  {selectedServices.map(service => (
                    <H2 margin="0 0 5px 0" key={service.id}>
                      {service.name}&nbsp;
                      <span className="services-available">
                        <ColorIcon
                          color={service.offered ? colors.sage : colors.danger}
                          margin="0 0 0 5px"
                          theme="filled"
                          type={service.offered ? 'check-circle' : 'close-circle'}
                        />
                      </span>
                    </H2>
                  ))}
                </div>
                {layout !== DIMENSIONS.MOBILE
                  && (
                    <MarketingButton
                      type="secondary"
                      shape="round"
                      margin="0 0 0 auto"
                      onClick={() => setShowAllServices(true)}
                    >
                      View All Practice Services
                    </MarketingButton>
                  )
                }
              </FlexRow>
              {layout === DIMENSIONS.MOBILE
                && (
                  <MarketingButton
                    type="secondary"
                    shape="round"
                    margin="20px 0 0 0"
                    onClick={() => setShowAllServices(true)}
                  >
                    View All Practice Services
                  </MarketingButton>
                )
              }
            </>
          )
        }
      </div>
    )
    : (
      <div>
        <FlexRow flexWrap="wrap" margin="0 0 0 20px">
          {listAllServices(allServicesMarked)}
        </FlexRow>
      </div>
    )
  ;
};

ServicesNeedToKnow.propTypes = {
  layout: PropTypes.string,
  primarySpecialty: PropTypes.number,
  providers: PropTypes.array,
  reasonExists: PropTypes.bool,
  subReasonIds: PropTypes.array,
  visitReason: PropTypes.object,
};

export default ServicesNeedToKnow;
